import React, { useState } from 'react';
import ShowcaseModal from '../components/ShowcaseModal/ShowcaseModal';
import showcaseData from '../data/showcase.json';
import './MiamiShowcase.css';

interface ShowcaseData {
  hero: any;
  featured: any;
  teamStats: any;
  news: any[];
  fixtures: any[];
  players: any[];
  videos: any;
  partners: any[];
  navigation: any;
}

const MiamiShowcase: React.FC = () => {
  const [activeTab, setActiveTab] = useState('mens');
  const data: ShowcaseData = showcaseData;
  const [modalContent, setModalContent] = useState<{ isOpen: boolean; content: string; title?: string; image?: string }>({
    isOpen: false,
    content: '',
    title: '',
    image: ''
  });

  const handleAction = (action: any, itemImage?: string) => {
    if (!action) return;

    if (action.type === 'modal') {
      setModalContent({
        isOpen: true,
        content: action.content || '',
        title: action.title,
        image: itemImage || action.image
      });
    } else if (action.type === 'link' && action.url) {
      window.open(action.url, '_blank');
    }
  };

  const closeModal = () => {
    setModalContent({ isOpen: false, content: '', title: '', image: '' });
  };

  return (
    <div className="gables-showcase">
      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-overlay">
          <div className="hero-content">
            <div className="hero-logo-circle">
              <img src="/logo.png" alt="Gables F.C. Logo" className="hero-logo" />
            </div>
            <h1 className="hero-title">{data.hero.title}</h1>
            <p className="hero-subtitle">{data.hero.subtitle}</p>
            <button className="hero-cta" onClick={() => window.location.href = 'mailto:siteadmin@gablesfc.com?subject=Join%20Gables%20F.C.&body=I%20am%20interested%20in%20joining%20Gables%20F.C.'}>
              JOIN THE CLUB
            </button>
          </div>
        </div>
        <div className="hero-stats">
          {data.hero.stats.map((stat: any, index: number) => (
            <div key={index} className="stat-item">
              <span className="stat-number">{stat.value}</span>
              <span className="stat-label">{stat.label}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Navigation Bar */}
      <nav className="gables-nav">
        <div className="nav-container">
          <div className="nav-logo">
            <img src="/logo.png" alt="GFC" className="nav-logo-img" />
            <span className="logo-text">GABLES FUTBOL CLUB</span>
          </div>
          <ul className="nav-menu">
            <li className="nav-item" onClick={() => document.getElementById('fixtures-section')?.scrollIntoView({ behavior: 'smooth' })}>
              FIXTURES
            </li>
            <li
              className={`nav-item ${!data.navigation.tickets.enabled ? 'coming-soon' : ''}`}
              onClick={() => handleAction(data.navigation.tickets.action)}
            >
              TICKETS
            </li>
            <li
              className={`nav-item ${!data.navigation.shop.enabled ? 'coming-soon' : ''}`}
              onClick={() => handleAction(data.navigation.shop.action)}
            >
              SHOP
            </li>
          </ul>
        </div>
      </nav>

      {/* Featured Content Grid */}
      <section className="featured-grid">
        <div className="grid-container">
          <div className="main-feature" onClick={() => handleAction(data.featured.main.action, data.featured.main.image)}>
            <div className="feature-image-container">
              <img src={data.featured.main.image} alt="Featured" />
              <div className="feature-overlay">
                <span className="feature-tag">{data.featured.main.tag}</span>
                <h2 className="feature-title">{data.featured.main.title}</h2>
                <p className="feature-excerpt">{data.featured.main.excerpt}</p>
              </div>
            </div>
          </div>

          <div className="side-features">
            {data.featured.side.map((item: any, index: number) => (
              <div key={index} className="side-feature-item" onClick={() => handleAction(item.action, item.image)}>
                <img src={item.image} alt={item.title} />
                <div className="side-feature-content">
                  <span className="category-tag">{item.category}</span>
                  <h3>{item.title}</h3>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Selector Tabs */}
      <section className="sport-tabs">
        <div className="tabs-container">
          <div className="tab-buttons">
            <button
              className={`tab-btn ${activeTab === 'mens' ? 'active' : ''}`}
              onClick={() => setActiveTab('mens')}
            >
              MEN'S TEAM
            </button>
          </div>

          <div className="tab-content">
            <div className="sport-info">
              <h3 className="sport-title">{data.teamStats.mens.title}</h3>
              <div className="sport-stats-row">
                <div className="sport-stat">
                  <span className="stat-value">{data.teamStats.mens.record}</span>
                  <span className="stat-desc">SEASON RECORD</span>
                </div>
                <div className="sport-stat">
                  <span className="stat-value">{data.teamStats.mens.position}</span>
                  <span className="stat-desc">LEAGUE POSITION</span>
                </div>
                <div className="sport-stat">
                  <span className="stat-value">{data.teamStats.mens.goalDifference}</span>
                  <span className="stat-desc">GOAL DIFFERENCE</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* News Section */}
      <section className="news-section">
        <div className="section-header">
          <h2 className="section-title">LATEST NEWS</h2>
          <a href="#news" className="view-all-link">VIEW ALL NEWS →</a>
        </div>

        <div className="news-grid">
          {data.news.map((item: any) => (
            <article key={item.id} className="news-card" onClick={() => handleAction(item.action, item.image)}>
              <div className="news-image-container">
                <img src={item.image} alt={item.title} />
                <span className="news-category">{item.category}</span>
              </div>
              <div className="news-content">
                <span className="news-date">{item.date}</span>
                <h3 className="news-title">{item.title}</h3>
                <a href="#" onClick={(e) => { e.preventDefault(); handleAction(item.action, item.image); }} className="read-more">
                  READ MORE →
                </a>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* Schedule Section */}
      <section className="schedule-section" id="fixtures-section">
        <div className="schedule-header">
          <h2 className="section-title">UPCOMING FIXTURES</h2>
          <div className="schedule-controls">
            <select className="sport-filter">
              <option>All Competitions</option>
            </select>
            <a href="#schedule" className="view-all-link">FULL FIXTURE LIST →</a>
          </div>
        </div>

        <div className="schedule-list">
          {data.fixtures.map((game: any, index: number) => (
            <div key={index} className="schedule-item" onClick={() => handleAction(game.action)}>
              <div className="game-date">
                <span className="date-text">{game.date}</span>
              </div>
              <div className="game-details">
                <h4 className="opponent">{game.opponent}</h4>
                <span className="location">{game.location} • {game.competition}</span>
              </div>
              <div className="game-time">
                <span className="time-text">{game.time}</span>
                <button
                  className={`ticket-btn ${!game.tickets?.available ? 'coming-soon' : ''}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (game.tickets?.url) {
                      window.open(game.tickets.url, '_blank');
                    } else {
                      handleAction(game.action);
                    }
                  }}
                >
                  {game.tickets?.available ? 'TICKETS' : 'INFO'}
                </button>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Player Stats Section */}
      <section className="news-section">
        <div className="section-header">
          <h2 className="section-title">TOP PERFORMERS</h2>
          <a href="#stats" className="view-all-link">FULL SQUAD →</a>
        </div>

        <div className="news-grid">
          {data.players.map((player: any, index: number) => (
            <div key={index} className="news-card" onClick={() => handleAction(player.action, player.image)}>
              <div className="news-image-container">
                <img src={player.image} alt={player.name} />
                <span className="news-category">{player.position}</span>
              </div>
              <div className="news-content">
                <h3 className="news-title">{player.name}</h3>
                <div style={{ display: 'flex', justifyContent: 'space-around', marginTop: '15px' }}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '24px', fontWeight: 'bold', color: 'var(--gables-gold)' }}>{player.goals}</div>
                    <div style={{ fontSize: '12px', color: 'var(--gables-gray)' }}>GOALS</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '24px', fontWeight: 'bold', color: 'var(--gables-gold)' }}>{player.assists}</div>
                    <div style={{ fontSize: '12px', color: 'var(--gables-gray)' }}>ASSISTS</div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Multimedia Section */}
      <section className="multimedia-section">
        <div className="section-header">
          <h2 className="section-title">GABLES FC TV</h2>
          <a href="#videos" className="view-all-link">COMING SOON →</a>
        </div>

        <div className="video-grid">
          <div className="main-video">
            <div className="video-placeholder" onClick={() => handleAction(data.videos.featured.action, data.videos.featured.image)}>
              <div className="play-button">▶</div>
              <img src={data.videos.featured.image} alt="Featured Video" />
            </div>
            <h3 className="video-title">{data.videos.featured.title}</h3>
            <p className="video-duration">{data.videos.featured.duration}</p>
          </div>

          <div className="video-sidebar">
            {data.videos.sidebar.map((video: any, index: number) => (
              <div key={index} className="video-item" onClick={() => handleAction(video.action, video.image)}>
                <img src={video.image} alt={video.title} />
                <div className="video-info">
                  <h4>{video.title}</h4>
                  <span className="duration">{video.duration}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Partners/Sponsors Section */}
      <section className="partners-section">
        <div className="partners-container">
          <h3 className="partners-title">OUR PARTNERS</h3>
          <div className="partners-grid">
            {data.partners.map((partner: any, index: number) => (
              <div
                key={index}
                className="partner-logo"
                onClick={() => handleAction(partner.action)}
                style={{ cursor: partner.action ? 'pointer' : 'default' }}
              >
                {partner.logo ? (
                  <img src={partner.logo} alt={partner.name} />
                ) : (
                  partner.name
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="gables-footer">
        <div className="footer-content">
          <div className="footer-section">
            <h4>CLUB</h4>
            <ul>
              <li>First Team</li>
              <li>History</li>
            </ul>
          </div>
          <div className="footer-section">
            <h4>FANS</h4>
            <ul>
              <li>Tickets</li>
              <li>Memberships</li>
              <li>Shop</li>
              <li>Match Day</li>
            </ul>
          </div>
          <div className="footer-section">
            <h4>CONNECT</h4>
            <ul>
              <li>Social Media</li>
              <li>Newsletter</li>
              <li>Mobile App</li>
              <li>Contact</li>
            </ul>
          </div>
          <div className="footer-section">
            <h4>GABLES F.C.</h4>
            <p className="footer-tagline">
              PRIDE • PASSION • EXCELLENCE
            </p>
            <div className="social-icons">
              <span className="social-icon">📘</span>
              <span className="social-icon">🐦</span>
              <span className="social-icon">📷</span>
              <span className="social-icon">📺</span>
            </div>
          </div>
        </div>
        <div className="footer-bottom">
          <p>© 2025 Gables Futbol Club. All Rights Reserved.</p>
        </div>
      </footer>

      {/* Modal Component */}
      <ShowcaseModal
        isOpen={modalContent.isOpen}
        onClose={closeModal}
        content={modalContent.content}
        title={modalContent.title}
        image={modalContent.image}
      />
    </div>
  );
};

export default MiamiShowcase;
